import React, { FunctionComponent, useCallback, useMemo } from 'react';
import { Checkbox } from '@mui/material';
import {
    Column,
    formatThousand,
    IconButton,
    makeRedirectionUrl,
    useSafeIntl,
} from 'bluesquare-components';

import { BreakWordCell } from 'Iaso/components/Cells/BreakWordCell';
import { DateTimeCell } from 'Iaso/components/Cells/DateTimeCell';
import { baseUrls } from 'Iaso/constants/urls';
import { getColor, useGetColors } from 'Iaso/hooks/useGetColors';
import { encodeUriSearches } from '../orgUnits/utils';
import { ProjectChip } from '../projects/components/ProjectChip';
import { TeamChip } from '../teams/components/TeamChip';
import { ActionsCell } from './components/ActionsCell';
import { PlanningStatusChip } from './components/PlanningStatusChip';
import { useDeletePlanning } from './hooks/requests/useDeletePlanning';
import { SavePlanningQuery } from './hooks/requests/useSavePlanning';
import MESSAGES from './messages';
import { Planning, SamplingResult } from './types';

type Props = {
    samplingResult: SamplingResult;
};

const ActionCell: FunctionComponent<Props> = ({ samplingResult }) => {
    const { data: colors } = useGetColors(true);
    const purpleColor = getColor(3, colors).replace('#', '');
    const urlParams: Record<string, any> = useMemo(
        () => ({
            locationLimit: 50000,
            order: 'id',
            pageSize: 50,
            page: 1,
            searchTabIndex: 0,
            searchActive: true,
            tab: 'map',
            isClusterActive: false,
            searches: encodeUriSearches([
                {
                    validation_status: 'VALID',
                    color: purpleColor,
                    group: `${samplingResult.group_id}`,
                },
            ]),
        }),
        [purpleColor, samplingResult],
    );

    return (
        <IconButton
            url={makeRedirectionUrl(baseUrls.orgUnits, urlParams)}
            icon="remove-red-eye"
            tooltipMessage={MESSAGES.seeSamplingResults}
        />
    );
};

export const usePlanningColumns = (params: any, count: number): Column[] => {
    const { formatMessage } = useSafeIntl();
    const { mutateAsync: deletePlanning } = useDeletePlanning({
        params,
        count,
    });
    return useMemo<Column[]>(
        () => [
            {
                Header: 'Id',
                accessor: 'id',
                width: 80,
            },
            {
                Header: formatMessage(MESSAGES.name),
                accessor: 'name',
                id: 'name',
            },
            {
                Header: formatMessage(MESSAGES.project),
                accessor: 'project__name',
                id: 'project__name',
                Cell: settings => (
                    <ProjectChip
                        project={settings.row.original.project_details}
                    />
                ),
            },
            {
                Header: formatMessage(MESSAGES.orgUnit),
                accessor: 'org_unit__name',
                id: 'org_unit__name',
                Cell: settings => settings.row.original.org_unit_details.name,
            },
            {
                Header: formatMessage(MESSAGES.startDate),
                accessor: 'started_at',
                id: 'started_at',
            },
            {
                Header: formatMessage(MESSAGES.endDate),
                accessor: 'ended_at',
                id: 'ended_at',
            },
            {
                Header: formatMessage(MESSAGES.team),
                accessor: 'team',
                id: 'team',
                Cell: settings => (
                    <TeamChip team={settings.row.original.team_details} />
                ),
            },
            {
                Header: formatMessage(MESSAGES.status),
                accessor: 'status',
                id: 'status',
                Cell: settings => (
                    <PlanningStatusChip status={settings.row.original.status} />
                ),
            },
            {
                Header: formatMessage(MESSAGES.actions),
                accessor: 'actions',
                resizable: false,
                sortable: false,
                Cell: settings => (
                    <ActionsCell
                        {...settings}
                        deletePlanning={deletePlanning}
                    />
                ),
            },
        ],
        [formatMessage, deletePlanning],
    );
};

export const useSamplingResultsColumns = (
    planning: Planning,
    savePlanning: (data: Partial<SavePlanningQuery>) => void,
): Column[] => {
    const { formatMessage } = useSafeIntl();

    const handleSelectSamplingResult = useCallback(
        (samplingResultId: number) => {
            savePlanning({
                id: planning.id,
                selected_sampling_result_id:
                    samplingResultId === planning.selected_sampling_result?.id
                        ? null
                        : samplingResultId,
            });
        },
        [savePlanning, planning.id, planning.selected_sampling_result?.id],
    );
    return useMemo<Column[]>(
        () => [
            {
                Header: 'Id',
                accessor: 'id',
                width: 80,
            },
            {
                Header: formatMessage(MESSAGES.samplingName),
                accessor: 'group_details.name',
                Cell: BreakWordCell,
            },
            {
                Header: formatMessage(MESSAGES.created_at),
                accessor: 'created_at',
                id: 'created_at',
                Cell: DateTimeCell,
            },
            {
                Header: 'Pipeline',
                accessor: 'pipeline_name',
            },
            {
                Header: formatMessage(MESSAGES.orgUnitsCount),
                accessor: 'group_details_org_unit_count',
                id: 'group_details_org_unit_count',
                sortable: false,
                Cell: settings =>
                    formatThousand(
                        settings.row.original.group_details.org_unit_count,
                    ),
            },
            {
                Header: formatMessage(MESSAGES.actions),
                accessor: 'actions',
                sortable: false,
                Cell: settings => (
                    <ActionCell samplingResult={settings.row.original} />
                ),
            },
            {
                Header: formatMessage(MESSAGES.selectSamplingResult),
                accessor: 'selected_sampling_result',
                Cell: settings => (
                    <Checkbox
                        checked={
                            planning.selected_sampling_result &&
                            settings.row.original.id ===
                                planning.selected_sampling_result?.id
                        }
                        onChange={() =>
                            handleSelectSamplingResult(settings.row.original.id)
                        }
                    />
                ),
            },
        ],
        [formatMessage, handleSelectSamplingResult, planning],
    );
};
